/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MoreMenuButton, Truncate } from '@modernloop/shared/components';
import { DIVIDER_ID } from '@modernloop/shared/constants';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { getExternalErrorMessage, logError } from '@modernloop/shared/utils';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  MenuItemProps,
  Stack,
  StackProps,
  Switch,
  SxProps,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  EmployeeFragmentDoc,
  ScheduleTaskDetails_TaskFragment,
  SourceType,
  TaskStatus,
  TaskTagsBase_TaskTagFragment,
  useTaskSetAssigneeMutation,
  useTaskSetIsUrgentMutation,
  useTaskSetTagsMutation,
  useTaskUpdateQueueMutation,
} from 'src/generated/mloop-graphql';

import EmployeeChip from 'src/components/EmployeeChip';
import Link from 'src/components/Link';
import UrgentTag from 'src/components/UrgentTag';
import { FCWithFragments } from 'src/components/types';

import Candidate from 'src/entities/Candidate';
import TaskTags from 'src/entities/Filters/TaskTags';
import QueueSelect from 'src/entities/Select/QueueSelect/QueueSelect';
import { useSubscribersSelectRefetch } from 'src/entities/SubscribersSelect/useSubscribersSelectRefetch';
import TaskName from 'src/entities/common/TaskName';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useEmployeeId from 'src/hooks/useEmployeeId';
import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

import { formatToTimeZone } from 'src/utils/datetime/Format';

import { NewBaseApplicationAlert } from 'src/views-new/ApplicationDetailsTaskView/ApplicationContent/ApplicationAlert';
import ApplicationConflict from 'src/views-new/ApplicationDetailsTaskView/ApplicationContent/ApplicationConflict';
import useMySchedulingTasksRefetch from 'src/views-new/ScheduleTaskDashboard/ScheduleTaskDashboardTables/useMySchedulingTasksRefetch';
import useScheduleTaskDashboardTaskSearchRefetch from 'src/views-new/ScheduleTaskDashboard/ScheduleTaskDashboardTables/useScheduleTaskDashboardTaskSearchRefetch';
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import AiAgentDelegate from './AiAgentDelegate';
import ScheduleTaskAssignee from './Common/ScheduleTaskAssignee';
import DelegateStatusChip from './DelegateStatusChip';
import ScheduleTaskFlags from './ScheduleTaskFlags';
import ScheduleTaskStatus from './ScheduleTaskStatus';
import ScheduleTaskStatusSubText from './ScheduleTaskStatusSubText';
import { CREATED_BY_VALUE_MARGIN_LEFT } from './alignment';

// eslint-disable-next-line modernloop/validate-component-definition.cjs
const ScheduleTaskDetailsRow = ({
  alignItems,
  label,
  component,
  sx,
}: {
  alignItems: StackProps['alignItems'];
  label: string | React.ReactNode;
  component: React.ReactNode | undefined;
  sx?: SxProps;
}) => {
  return (
    <Stack direction="row" alignItems={alignItems} spacing={2} sx={{ ...sx, minHeight: '28px' }}>
      {!React.isValidElement(label) && (
        <Typography variant="body2" color="text.secondary" fontWeight={600} style={{ flexShrink: 0, width: '80px' }}>
          {label}
        </Typography>
      )}
      {React.isValidElement(label) && label}
      {component}
    </Stack>
  );
};

type ScheduleTaskDetailsFragmentProps = {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  task: ScheduleTaskDetails_TaskFragment;
};

export type ScheduleTaskMenuItemProps = MenuItemProps & {
  icon?: React.ReactNode;
};

type Props = {
  moreMenuOptions?: ScheduleTaskMenuItemProps[];
  onMenuOptionSelected?: (option: ScheduleTaskMenuItemProps) => void;
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const ScheduleTaskDetails: FCWithFragments<ScheduleTaskDetailsFragmentProps, Props> = ({
  task,
  moreMenuOptions,
  onMenuOptionSelected,
}) => {
  const aiTaskAgentsEnabled = useIsAiTaskEnabled();
  const { t } = useTranslation();
  const [taskSetTagsMutation] = useTaskSetTagsMutation();
  const [taskSetAssigneeMutation] = useTaskSetAssigneeMutation();
  const [taskSetIsUrgentMutation] = useTaskSetIsUrgentMutation();
  const [taskUpdateQueueMutation] = useTaskUpdateQueueMutation();
  const refetchActivityFeed = useActivityFeedRefetch(SourceType.SourceTypeTask);
  const refetchSubscriberSelect = useSubscribersSelectRefetch();
  const refetchScheduleTaskDashboardTaskSearch = useScheduleTaskDashboardTaskSearchRefetch();
  const refetchMySchedulingTasks = useMySchedulingTasksRefetch();

  const employeeId = useEmployeeId();
  const { enqueueSnackbar } = useSnackbar();

  const [assigningToMe, setAssigningToMe] = useState(false);

  const jobName = useMemo(() => {
    return task.jobStage?.job?.name ?? task.application.job?.name ?? '';
  }, [task.application.job?.name, task.jobStage?.job?.name]);

  const jobId = useMemo(() => {
    return task.jobStage?.job?.id ?? task.application.job?.id;
  }, [task.application.job?.id, task.jobStage?.job?.id]);

  const jobNameContent = useMemo(() => {
    if (!jobName) {
      return null;
    }

    return (
      <strong style={{ fontWeight: 600 }}>
        {jobName}
        {task.jobStage?.job?.atsJob?.atsCustomerFriendlyId ? (
          <>
            {' '}
            (
            <Truncate
              text={task.jobStage.job.atsJob.atsCustomerFriendlyId}
              maxChars={4}
              variant="hide-end"
              typographyVariant="body2"
              typographyProps={{ fontWeight: 600 }}
            />
            )
          </>
        ) : (
          ''
        )}
      </strong>
    );
  }, [jobName, task.jobStage?.job?.atsJob?.atsCustomerFriendlyId]);

  const handleTagsChange = useCallback(
    (tags: TaskTagsBase_TaskTagFragment[]) => {
      taskSetTagsMutation({
        variables: {
          input: {
            taskId: task.id,
            tagIds: tags.map((tag) => tag.id),
          },
        },
        optimisticResponse: {
          taskSetTags: {
            __typename: 'Task',
            id: task.id,
            tags,
          },
        },
      })
        .then(() => refetchActivityFeed())
        .catch((e) => {
          enqueueSnackbar(getExternalErrorMessage(e), { variant: 'error' });
        });
    },
    [enqueueSnackbar, refetchActivityFeed, task.id, taskSetTagsMutation]
  );

  const handleTaskQueueChange = useCallback(
    async (queueId) => {
      try {
        await taskUpdateQueueMutation({
          variables: {
            input: {
              taskId: task.id,
              taskQueueId: queueId,
            },
          },
        });
        refetchMySchedulingTasks();
        refetchActivityFeed();
      } catch (error) {
        logError(`Failed to update task queue ${error}`);
        enqueueSnackbar(getExternalErrorMessage(error), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, refetchActivityFeed, refetchMySchedulingTasks, task.id, taskUpdateQueueMutation]
  );

  const handleChangeTaskUrgency = useCallback(
    (value: boolean) => {
      // eslint-disable-next-line promise/catch-or-return
      taskSetIsUrgentMutation({
        variables: { input: { taskId: task.id, isUrgent: value } },
        optimisticResponse: { taskSetIsUrgent: { id: task.id, isUrgent: value, __typename: 'Task' } },
      }).then(() => refetchActivityFeed());
    },
    [refetchActivityFeed, task.id, taskSetIsUrgentMutation]
  );

  const handleTaskSetAssignee = useCallback(() => {
    if (!employeeId) {
      return;
    }

    setAssigningToMe(true);

    // eslint-disable-next-line promise/catch-or-return
    taskSetAssigneeMutation({
      variables: {
        input: {
          taskId: task.id,
          assigneeEmployeeId: employeeId,
        },
      },
    }) // eslint-disable-next-line promise/always-return
      .then(() => {
        refetchSubscriberSelect();
        refetchScheduleTaskDashboardTaskSearch();
        refetchMySchedulingTasks();
        refetchActivityFeed();
      })
      .catch((e) => {
        enqueueSnackbar(e?.message, { variant: 'error' });
      })
      .finally(() => setAssigningToMe(false));
  }, [
    employeeId,
    enqueueSnackbar,
    refetchActivityFeed,
    refetchMySchedulingTasks,
    refetchScheduleTaskDashboardTaskSearch,
    refetchSubscriberSelect,
    task.id,
    taskSetAssigneeMutation,
  ]);

  return (
    <Stack spacing={1.5}>
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h4" fontWeight={600}>
              {t('hiring-dashboard-tasks:tasks.details.schedule')} -&nbsp;
              <TaskName task={task} />
            </Typography>
            {moreMenuOptions?.length && onMenuOptionSelected && (
              <MoreMenuButton
                iconSx={{
                  height: '24px',
                  width: '32px',
                  borderRadius: '4px',
                  backgroundColor: (theme) => theme.palette.background.contrast,
                }}
                dataTestId="schedule-task-details-more-menu-button"
                color="min-contrast-grey"
              >
                {moreMenuOptions.map((option) => {
                  if (option.id === DIVIDER_ID) {
                    return <Divider key={option.id} />;
                  }
                  return (
                    <MenuItem
                      data-testid={`schedule-task-details-more-menu-item-${option.id}`}
                      key={option.id}
                      onClick={() => onMenuOptionSelected(option)}
                    >
                      <Typography color={option.color} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon}
                        {option.value}
                      </Typography>
                    </MenuItem>
                  );
                })}
              </MoreMenuButton>
            )}
          </Stack>
        }
        component={undefined}
      />
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={
          <Typography
            variant="body2"
            color="text.secondary"
            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}
          >
            {task.application.candidate && (
              <Candidate
                candidate={task.application.candidate}
                applicationId={task.application.id}
                onClick={() => {
                  SidePanelManager.closeSidePanel();
                }}
                variant="subtitle2"
              />
            )}
            {jobNameContent ? (
              <>
                {t('hiring-dashboard-tasks:tasks.details.for')}{' '}
                {jobId ? (
                  <Link
                    component={RouterLink}
                    to={`/jobs/${jobId}`}
                    color="inherit"
                    variant="body2"
                    onClick={() => {
                      SidePanelManager.closeSidePanel();
                    }}
                  >
                    {jobNameContent}
                  </Link>
                ) : (
                  jobNameContent
                )}
              </>
            ) : null}
            {task.jobStage?.job?.location?.map((loc) => {
              return <Chip variant="outlined" key={loc} label={loc} size="small" />;
            })}
          </Typography>
        }
        component={undefined}
        sx={{ marginTop: '4px !important' }}
      />
      <Stack>
        <NewBaseApplicationAlert status={task.application.status} />
      </Stack>
      <ApplicationConflict task={task} />
      <ScheduleTaskDetailsRow
        alignItems="baseline"
        label={t('hiring-dashboard-tasks:tasks.column.status')}
        component={
          <Stack spacing={0.5}>
            <Box mt="5px">
              <ScheduleTaskStatus status={task.status} />
            </Box>
            <ScheduleTaskStatusSubText task={task} />
          </Stack>
        }
      />
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={t('hiring-dashboard-tasks:tasks.column.urgent')}
        component={
          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              size="small"
              disabled={task.status === TaskStatus.Canceled}
              checked={task.isUrgent}
              onChange={(_, checked) => handleChangeTaskUrgency(checked)}
            />
            {task?.isUrgent && <UrgentTag size="small" />}
          </Stack>
        }
      />
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={t('hiring-dashboard-tasks:tasks.column.assignee')}
        component={
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexGrow: 1, marginLeft: '8px !important', minWidth: 0 }}
          >
            <ScheduleTaskAssignee
              shouldRefecthTaskSearch
              shouldRefetchMySchedulingTasks
              shouldRefetchSubscribersList
              task={task}
            />
            {assigningToMe && <CircularProgress size={20} />}
            {!task.assignee &&
              !assigningToMe && (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <Link
                  color="primary"
                  onClick={handleTaskSetAssignee}
                  variant="body2"
                  fontWeight={500}
                  sx={{ textDecoration: 'none', flexShrink: 0 }}
                >
                  {t('hiring-dashboard-tasks:tasks.details.assignToMe')}
                </Link>
              )}
          </Stack>
        }
      />
      {/* Delegate — Linear-style agent delegation below assignee */}
      {aiTaskAgentsEnabled && (
        <ScheduleTaskDetailsRow
          alignItems="center"
          label={t('hiring-dashboard-schedule:schedule.scheduleTask.delegate')}
          component={
            <Stack direction="row" alignItems="center" spacing={1}>
              <AiAgentDelegate taskId={task.id} delegateType={task.delegateType} />
              {task.delegateType === 'AGENT' && task.delegateStatus && (
                <DelegateStatusChip delegateStatus={task.delegateStatus} />
              )}
            </Stack>
          }
        />
      )}
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={t('hiring-dashboard-tasks:tasks.column.queue')}
        component={
          <Stack direction="row" spacing={1} alignItems="center">
            <QueueSelect
              groupQueue
              onOptionChange={handleTaskQueueChange}
              defaultTaskQueueId={task.application.job?.jobSettings.defaultTaskQueue?.id}
              selectedTaskQueueId={task.taskQueue?.id}
            />
          </Stack>
        }
      />
      <ScheduleTaskDetailsRow
        label={t('hiring-dashboard-tasks:tasks.column.createdBy')}
        alignItems="center"
        component={
          <Box
            data-testid="schedule-task-details-created-by-value"
            sx={{ marginLeft: `${CREATED_BY_VALUE_MARGIN_LEFT}px !important` }}
          >
            <EmployeeChip
              name={task.createdBy.fullName || ''}
              photoSrc={task.createdBy.internalProfilePictureUrl || ''}
              variant="filled"
            />
          </Box>
        }
      />
      <ScheduleTaskDetailsRow
        label={t('hiring-dashboard-tasks:tasks.tab.created')}
        alignItems="center"
        component={
          <Typography variant="body2">
            {`${formatToTimeZone(task.createdAt, 'MMM d, yyyy', getLocalTimezone())}  at ${formatToTimeZone(
              task.createdAt,
              'h:mm aaa',
              getLocalTimezone()
            )}`}
          </Typography>
        }
      />
      <ScheduleTaskDetailsRow
        alignItems="center"
        label={t('hiring-dashboard-tasks:tasks.column.stage')}
        component={
          <Typography variant="body2">
            {task.jobStage?.name || t('hiring-dashboard-tasks:tasks.status.unknown')}
          </Typography>
        }
      />
      <ScheduleTaskDetailsRow
        alignItems="baseline"
        label={
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            style={{ flexShrink: 0, width: '80px', marginTop: '5px' }}
          >
            {t('hiring-dashboard-tasks:tasks.column.tags')}
          </Typography>
        }
        component={<TaskTags selectedTaskTags={task.tags} onChange={handleTagsChange} />}
      />
      <ScheduleTaskDetailsRow
        alignItems="flex-start"
        label={
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            style={{ flexShrink: 0, width: '80px', marginTop: '5px' }}
          >
            {t('hiring-dashboard-tasks:tasks.column.flags')}
          </Typography>
        }
        component={
          <Box pt={0.5}>
            <ScheduleTaskFlags taskFlags={task.flags} />
          </Box>
        }
      />
    </Stack>
  );
};

ScheduleTaskDetails.fragments = {
  task: gql`
    ${TaskTags.fragments.selectedTaskTags}
    ${ScheduleTaskFlags.fragments.taskFlags}
    ${ScheduleTaskAssignee.fragments.task}
    ${EmployeeFragmentDoc}
    ${ScheduleTaskStatusSubText.fragments.task}
    ${TaskName.fragments.task}
    ${Candidate.fragments.candidate}
    ${ApplicationConflict.fragments.task}
    fragment ScheduleTaskDetails_task on Task {
      id
      delegateType
      delegateStatus
      isUrgent
      name
      status
      application {
        id
        job {
          id
          name
          jobSettings {
            id
            defaultTaskQueue {
              id
            }
          }
        }
        status
        candidate {
          id
          fullName

          ...Candidate_candidate
        }
      }
      taskQueue {
        id
      }
      createdBy {
        ...Employee
      }
      createdAt
      jobStage {
        id
        name
        job {
          id
          name
          location
          atsJob {
            atsCustomerFriendlyId
          }
        }
      }
      tags {
        ...TaskTags_selectedTaskTag
      }
      flags {
        ...ScheduleTaskFlags_taskFlag
      }
      ...TaskName_task
      ...ScheduleTaskAssignee_task
      ...ScheduleTaskStatusSubText_task
      ...ApplicationConflict_task
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetTagsMutation = gql`
  ${TaskTags.fragments.selectedTaskTags}
  mutation TaskSetTags($input: TaskSetTagsInput!) {
    taskSetTags(input: $input) {
      id
      tags {
        ...TaskTags_selectedTaskTag
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetQueueMutation = gql`
  mutation TaskUpdateQueue($input: TaskUpdateInput!) {
    taskUpdate(input: $input) {
      id
      taskQueue {
        id
      }
    }
  }
`;

export default ScheduleTaskDetails;
